import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import type { ReactNode } from 'react';

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success';
export type AlertType =
  | 'order_lock'
  | 'signature_lock'
  | 'order_error'
  | 'general'
  | 'download'
  | 'preview'
  | 'summary';

export type AlertContext = 'fileManager' | 'summary' | 'renter';

export interface CertificationAlert {
  id: string;
  type: AlertType;
  timeout: number;
  createdAt: number;
  severity: AlertSeverity;
  message: ReactNode;
  icon?: ReactNode;
  read: boolean;
  completed: boolean;
  totalElapsedTime: number;
}

interface CertificationAlertsState {
  fileManager: CertificationAlert[];
  summary: CertificationAlert[];
  renter: CertificationAlert[];
}

const initialState: CertificationAlertsState = {
  fileManager: [],
  summary: [],
  renter: [],
};

const certificationAlertsSlice = createSlice({
  name: 'certificationAlerts',
  initialState,
  reducers: {
    addAlert: (
      state,
      action: PayloadAction<{
        context: AlertContext;
        alert: Omit<
          CertificationAlert,
          'id' | 'createdAt' | 'read' | 'completed' | 'totalElapsedTime'
        >;
      }>
    ) => {
      const { context, alert } = action.payload;
      const newAlert: CertificationAlert = {
        ...alert,
        id: uuidv4(),
        createdAt: Date.now(),
        timeout: alert.timeout || 7500, // Default to 7.5 seconds
        read: false,
        completed: false,
        totalElapsedTime: 0,
      };
      state[context].push(newAlert);
    },
    updateAlert: (
      state,
      action: PayloadAction<{
        context: AlertContext;
        id: string;
        updates: Partial<Omit<CertificationAlert, 'id' | 'createdAt'>>;
      }>
    ) => {
      const { context, id, updates } = action.payload;
      const alert = state[context].find((a) => a.id === id);
      if (alert) {
        Object.assign(alert, updates);
        // Reset createdAt to restart the timer
        alert.createdAt = Date.now();
      }
    },
    removeAlert: (
      state,
      action: PayloadAction<{ context: AlertContext; id: string }>
    ) => {
      const { context, id } = action.payload;
      state[context] = state[context].filter((alert) => alert.id !== id);
    },
    clearAllAlerts: (
      state,
      action: PayloadAction<{ context?: AlertContext }>
    ) => {
      const { context } = action.payload;
      if (context) {
        state[context] = state[context].map((alert) => ({
          ...alert,
          completed: true,
        }));
      } else {
        // Clear all contexts
        state.fileManager = state.fileManager.map((alert) => ({
          ...alert,
          completed: true,
        }));
        state.summary = state.summary.map((alert) => ({
          ...alert,
          completed: true,
        }));
      }
    },
    clearAlertsByType: (
      state,
      action: PayloadAction<{ context: AlertContext; type: AlertType }>
    ) => {
      const { context, type } = action.payload;
      state[context] = state[context].map((alert) =>
        alert.type === type ? { ...alert, completed: true } : alert
      );
    },
    markAlertAsRead: (
      state,
      action: PayloadAction<{ context: AlertContext; id: string }>
    ) => {
      const { context, id } = action.payload;
      const alert = state[context].find((a) => a.id === id);
      if (alert) {
        alert.read = true;
      }
    },
    markAlertAsCompleted: (
      state,
      action: PayloadAction<{ context: AlertContext; id: string }>
    ) => {
      const { context, id } = action.payload;
      const alert = state[context].find((a) => a.id === id);
      if (alert) {
        alert.completed = true;
      }
    },
    updateAlertElapsedTime: (
      state,
      action: PayloadAction<{
        context: AlertContext;
        id: string;
        elapsedTime: number;
      }>
    ) => {
      const { context, id, elapsedTime } = action.payload;
      const alert = state[context].find((a) => a.id === id);
      if (alert) {
        alert.totalElapsedTime = elapsedTime;
      }
    },
  },
});

export const {
  addAlert,
  updateAlert,
  removeAlert,
  clearAllAlerts,
  clearAlertsByType,
  markAlertAsRead,
  markAlertAsCompleted,
  updateAlertElapsedTime,
} = certificationAlertsSlice.actions;

export default certificationAlertsSlice.reducer;
